import { sound } from '@pixi/sound';
import { v4 as uuid } from 'uuid';

import {
    Application,
    Assets,
    Sprite,
    TilingSprite,
    Text,
    RenderLayer,
    Container,
    Point,
} from 'pixi.js';

class Downfall {
    constructor(game, options = {}, state = {}, onHit = () => {}, onEnd = () => {}) {
        this.app = null;
        this.started = false;
        this.running = false;
        this.frames = 0;
        this.ticks = 0;

        this.playerSpeed = 3;
        this.playerFriction = 0.95;
        this.playerAcceleration = 0.9;

        this.dropFrequency = 100;

        this.onHit = onHit;
        this.onEnd = onEnd;

        const {
            theme = {},
            backgroundImage = null,
            foregroundImage = null,
            playerImage = null,
            pointsImage = null,
            choices = null,
            grid = null,
        } = game || {};

        const { columns = null } = grid || {};
        this.columns = columns || null;
        this.centerOffsetPct = 0.1;

        const {
            scrollingBackground = false,
            scrollingDirection = 'down',
            canDragPlayer = false,
            backgroundColor = null,
            fontFamily = null,
            hasCountdown = false,
            hasPoints = false,
            padding = 200,
        } = theme || {};

        const { initialPoints = 0 } = state || {};
        this.points = initialPoints;

        const { winningScore = 10, speed = 2 } = options || {};

        // console.log('game', game);
        // console.log('padding', padding);
        // console.log('speed', speed);

        this.width = null;
        this.height = null;

        this.winningScore = winningScore;
        this.scrollingBackground = scrollingBackground;
        this.scrollingDirection = scrollingDirection;

        this.canDragPlayer = canDragPlayer;
        this.speed = speed || 2;
        this.fontFamily = fontFamily;
        this.backgroundColor = backgroundColor || '#1099bb';
        this.hasCountdown = hasCountdown;
        this.hasPoints = hasPoints;

        this.background = null;
        this.foreground = null;
        this.player = null;

        this.enemyContainer = null;
        this.enemyLayer = null;
        this.countdownInterval = null;
        this.hitPointsTimeout = null;
        this.padding = padding;

        this.choiceAssets = choices;

        this.assets = {};
        if (backgroundImage !== null) {
            this.assets.background = backgroundImage.url;
        }
        if (foregroundImage !== null) {
            this.assets.foreground = foregroundImage.url;
        }
        if (playerImage !== null) {
            this.assets.player = playerImage.url;
        }
        if (pointsImage !== null) {
            this.assets.points = pointsImage.url;
        }

        this.choices = [];
        this.obstacles = [];

        this.onClick = this.onClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.onDragMove = this.onDragMove.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onDragEnd = this.onDragEnd.bind(this);

        this.main = this.main.bind(this);
        this.resize = this.resize.bind(this);
        this.addEvents = this.addEvents.bind(this);
    }

    async create(gameRef, width = null, height = null) {
        this.started = true;

        // Initialize game state and start the game
        const app = new Application();
        await app.init({
            background: this.backgroundColor,
            width,
            height,
            // resizeTo: containerRef.current !== null ? containerRef.current : null,
        });

        gameRef.current.appendChild(app.canvas);

        this.width = width;
        this.height = height;
        this.app = app;
        return app;
    }

    async init() {
        await this.loadAssets();
        await this.createBackground();
        await this.createForeground();
        await this.createEnemies();
        await this.createPlayer();

        if (this.hasPoints) {
            await this.createPoints();
        }

        await this.createHitPoints();

        if (this.hasCountdown) {
            await this.createCountdown();
        }

        this.removeEvents();
        this.addEvents();

        if (this.hasCountdown && !this.running) {
            this.countdown();
        } else {
            this.running = true;
        }

        this.main();
    }

    async loadAssets() {
        (this.choiceAssets || []).map((item) => {
            const { id = null, image = null, audio = null } = item || {};
            if (image !== null) {
                Assets.add({ alias: id, src: item.image.url });
            }
            if (audio !== null) {
                sound.add(id, audio.url);
            }
            this.choices.push({
                id: item.id,
                points: item.value,
                sound: audio !== null ? id : null,
            });
        });

        Object.keys(this.assets).forEach((key) => {
            Assets.add({ alias: key, src: this.assets[key] });
        });

        await Assets.load(this.choices.map((item) => item.id));
    }

    async createBackground() {
        const texture = await Assets.load('background');
        const tilingSprite = new TilingSprite({
            texture,
            // width: this.app.screen.width,
            // height: this.app.screen.height,
            x: this.app.screen.width / 2,
            y: this.app.screen.height / 2,
            anchor: { x: 0.5, y: 0.5 },
        });
        this.background = tilingSprite;
        this.app.stage.addChild(tilingSprite);

        const width = Math.min(this.app.screen.width, this.background.width);
        this.leftBound = this.padding || 0;
        this.rightBound = width - (this.padding || 0);
    }

    async createForeground() {
        const texture = await Assets.load('foreground');
        if (texture === null) {
            return;
        }

        const { frame = null } = texture || {};
        const { width = null } = frame || {};
        const tilingSprite = new TilingSprite({
            texture,
            width: width !== null ? width : this.app.screen.width,
            height: this.app.screen.height,
            x: this.app.screen.width / 2,
            y: this.app.screen.height / 2,
            anchor: { x: 0.5, y: 0.5 },
        });
        this.foreground = tilingSprite;
        this.app.stage.addChild(tilingSprite);

        this.leftBound = this.foreground.x - this.foreground.width / 2;
        this.rightBound = this.foreground.x + this.foreground.width / 2;
    }

    async createEnemies() {
        const enemyLayer = new RenderLayer();
        this.enemyLayer = enemyLayer;
        this.app.stage.addChild(enemyLayer);

        const container = new Container();
        this.enemyContainer = container;
        this.app.stage.addChild(container);
    }

    async createPlayer() {
        const texture = await Assets.load('player');
        if (texture === null) {
            return;
        }

        const player = new Sprite(texture);
        player.anchor.set(0.5);
        player.x = this.app.screen.width / 2;
        player.y = this.app.screen.height - this.app.screen.height / 10;
        player.acceleration = new Point(0);
        player.mass = 1;
        player.scale = 0.5;

        this.player = player;
        this.app.stage.addChild(player);
    }

    async createPoints() {
        const pointsText = new Text({
            text: `${this.points} pts`,
            style: {
                fontFamily: this.fontFamily || 'Arial',
                fontSize: 16,
                fill: 0xffffff,
                align: 'center',
                lineHeight: 1,
                padding: 5,
            },
        });

        pointsText.x = 25;
        pointsText.y = 20;

        this.pointsText = pointsText;
        this.app.stage.addChild(pointsText);
    }

    async createHitPoints() {
        this.hitPoints = new Container();
        this.hitPoints.x = this.app.screen.width / 2;
        this.hitPoints.y = this.app.screen.height / 2;

        const texture = await Assets.load('points');

        if (texture !== null) {
            const hitPointsTexture = new Sprite(texture);
            hitPointsTexture.anchor.set(0.5);
            hitPointsTexture.scale = 0.66;
            this.hitPointsTexture = hitPointsTexture;
            this.hitPoints.addChild(hitPointsTexture);
        }

        const hitPointsText = new Text({
            text: 0,
            style: {
                fontFamily: this.fontFamily || 'Arial',
                fontSize: 20,
                fill: 0x000000,
                align: 'center',
                lineHeight: 1,
                padding: 5,
            },
        });
        hitPointsText.anchor.set(0.5, 0.5);
        hitPointsText.x = 4;
        hitPointsText.y = 9;
        this.hitPoints.addChild(hitPointsText);

        this.hitPoints.alpha = 0;
        this.app.stage.addChild(this.hitPoints);
    }

    async createCountdown() {
        // Create the points display
        const countdownText = new Text({
            text: '3',
            style: {
                fontFamily: this.fontFamily || 'Arial',
                fontSize: 64,
                fill: 0xffffff,
                align: 'center',
                fontWeight: 'bold',
                lineHeight: 1,
                padding: 10,
            },
        });

        countdownText.anchor.set(0.5);
        countdownText.x = this.app.screen.width / 2;
        countdownText.y = this.app.screen.height / 2;

        this.countdownText = countdownText;
        this.app.stage.addChild(countdownText);
    }

    addEvents() {
        this.app.stage.eventMode = 'static';
        this.app.stage.cursor = 'pointer';
        this.app.stage.hitArea = this.app.screen;

        if (this.canDragPlayer) {
            this.app.stage.on('pointerdown', this.onDragStart);
            this.app.stage.on('pointerup', this.onDragEnd);
            this.app.stage.on('pointerupoutside', this.onDragEnd);
        } else {
            this.app.stage.on('pointerdown', this.onClick);
        }

        if (typeof document !== 'undefined') {
            document.addEventListener('keydown', this.onKeyDown);
        }
    }

    removeEvents() {
        if (this.canDragPlayer) {
            this.app.stage.off('pointerdown', this.onDragStart);
            this.app.stage.off('pointerup', this.onDragEnd);
            this.app.stage.off('pointerupoutside', this.onDragEnd);
        } else {
            this.app.stage.off('pointerdown', this.onClick);
        }

        if (typeof document !== 'undefined') {
            document.removeEventListener('keydown', this.onKeyDown);
        }
    }

    async addEnemy() {
        if (!this.running) {
            return;
        }

        const container = new Container();
        const bonuses = this.choices.filter((item) => item.points >= 0) || [];
        const lessThanZero = this.choices.filter((item) => item.points < 0) || [];
        const penalties = lessThanZero.length > 0 ? lessThanZero : bonuses;
        const items = Math.random() < 0.5 ? bonuses : penalties;

        const obstacle = items[Math.floor(Math.random() * items.length)];
        const texture = await Assets.load(obstacle.id);

        const baseWidth = this.rightBound - this.leftBound;
        const offset = baseWidth * this.centerOffsetPct;
        const width = baseWidth - offset;

        let positionX = width / 2;
        if (this.columns !== null) {
            const columnWidth = width / this.columns;
            const columnIndex = Math.floor(Math.random() * this.columns);
            const columnX = columnWidth * columnIndex + columnWidth / 2 + this.leftBound + offset;
            // console.log('columnX', columnX);
            positionX = columnX;
        } else {
            positionX = Math.random() * width + this.leftBound + offset;
        }

        if (obstacle.id.indexOf('left') !== -1) {
            positionX = this.leftBound;
        } else if (obstacle.id.indexOf('right') !== -1) {
            positionX = this.rightBound;
        }

        const enemy = new Sprite(texture);
        enemy.anchor.set(0.5);
        enemy.x = positionX;
        enemy.y = -enemy.height; // Start above the screen
        enemy.scale = 0.5;
        container.addChild(enemy);

        container.uuid = uuid();
        container.points = obstacle.points;
        container.sound = obstacle.sound;

        this.obstacles.push(container);
        this.enemyContainer.addChild(container);
    }

    showHitPoints(points) {
        clearTimeout(this.hitPointsTimeout);
        this.hitPoints.alpha = 1;
        this.hitPoints.x = this.player.x;
        this.hitPoints.y = this.player.y - 100;

        const text = this.hitPoints.children.find((child) => child instanceof Text) || null;
        if (text !== null) {
            text.text = points > 0 ? `+${points}` : points;
        }
        const sprite = this.hitPoints.children.find((child) => child instanceof Sprite) || null;
        if (sprite !== null) {
            sprite.tint = points > 0 ? 0xffffff : 0xff0000;
        }

        this.hitPointsTimeout = setTimeout(() => {
            this.hitPoints.alpha = 0;
        }, 1000);
    }

    countdown() {
        const countdownDuration = 3; // Countdown duration in seconds
        let countdownValue = countdownDuration;
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        this.countdownInterval = setInterval(() => {
            if (countdownValue > 0) {
                countdownValue -= 1;
                if (countdownValue === 0) {
                    this.countdownText.text = 'GO!';
                } else {
                    this.countdownText.text = countdownValue.toString();
                }
            } else {
                clearInterval(this.countdownInterval);
                this.countdownInterval = null;
                this.countdownText.text = '';
                this.running = true;
            }
        }, 1000);
    }

    main() {
        this.app.ticker.add((time) => {
            const delta = time.deltaTime;
            this.frames += delta;
            this.ticks += 1;

            if (this.player) {
                this.player.x += this.player.acceleration.x * this.playerSpeed;
                const next =
                    this.player.acceleration.x > 0.01 || this.player.acceleration.x < -0.01
                        ? this.player.acceleration.x * this.playerFriction
                        : 0;
                this.player.acceleration.set(next, 0);
                this.player.rotation = next / 4;
            }

            if (this.scrollingBackground) {
                if (this.background !== null) {
                    this.background.tilePosition.y += this.speed / 1;
                }
                if (this.foreground !== null) {
                    this.foreground.tilePosition.y += this.speed / 2;
                }
            }

            if (Math.floor(this.ticks) % this.dropFrequency === 0) {
                this.addEnemy();
            }

            for (let i = 0; i < this.obstacles.length; i++) {
                const enemy = this.obstacles[i];
                if (typeof enemy !== 'undefined') {
                    enemy.y += this.speed / 2;
                    if (enemy.y > this.app.screen.height + enemy.height + 100) {
                        this.enemyContainer.removeChild(enemy);
                        this.obstacles.splice(i, 1);
                    }

                    // Check for collision with player
                    if (this.collide(this.player, enemy)) {
                        this.points += enemy.points;
                        this.points = Math.max(this.points, 0);

                        if (this.hasPoints) {
                            this.pointsText.text = `${this.points} pts`;
                        }

                        // Handle collision logic
                        this.enemyContainer.removeChild(enemy);
                        this.obstacles.splice(i, 1);
                        this.showHitPoints(enemy.points, this.player.x, this.player.y);

                        if (!this.muted && enemy.sound !== null) {
                            sound.play(enemy.sound);
                        }

                        if (this.onHit !== null) {
                            this.onHit(this.points);
                        }

                        // End condition
                        if (this.points >= this.winningScore) {
                            this.removeEvents();
                            this.app.ticker.stop();
                            this.started = false;
                            this.running = false;
                            if (this.onEnd !== null) {
                                this.onEnd(this.points);
                            }
                        }
                    }
                }
            }
        });
    }

    onClick(event) {
        const { screen } = event || {};
        const { x = 0 } = screen || {};
        const centerX = this.app.screen.width / 2;
        if (x >= centerX) {
            this.movePlayer('right');
        } else if (x < centerX) {
            this.movePlayer('left');
        }
    }

    onKeyDown(event) {
        const { key = null, keyCode = null } = event || {};
        if (key === 'ArrowLeft' || keyCode === 37) {
            this.movePlayer('left');
        } else if (key === 'ArrowRight' || keyCode === 39) {
            this.movePlayer('right');
        }
    }

    onDragMove(event) {
        if (this.dragTarget) {
            this.dragTarget.parent.toLocal(event.global, null, this.dragTarget.position);
        }
    }

    onDragStart() {
        this.alpha = 0.5;
        this.dragTarget = this.player;
        this.app.stage.on('pointermove', this.onDragMove);
    }

    onDragEnd() {
        if (this.dragTarget) {
            this.app.stage.off('pointermove', this.onDragMove);
            this.dragTarget.alpha = 1;
            this.dragTarget = null;
        }
    }

    movePlayer(direction) {
        if (direction === 'left' && this.player.x >= this.leftBound + this.player.width) {
            this.player.acceleration.set(-this.playerAcceleration, 0);
        } else if (direction === 'right' && this.player.x <= this.rightBound - this.player.width) {
            this.player.acceleration.set(this.playerAcceleration, 0);
        }
    }

    collide(object1 = null, object2 = null) {
        if (object1 === null || object2 === null) {
            return false;
        }
        const bounds1 = object1.getBounds();
        const bounds2 = object2.getBounds();
        return (
            bounds1.x < bounds2.x + bounds2.width &&
            bounds1.x + bounds1.width > bounds2.x &&
            bounds1.y < bounds2.y + bounds2.height &&
            bounds1.y + bounds1.height > bounds2.y
        );
    }

    resize(width, height) {
        if (this.app) {
            this.app.renderer.resize(width, height);

            const scaleX = width / this.width;
            const scaleY = height / this.height;

            if (this.background !== null) {
                this.background.scale.set(scaleX, scaleY);
                this.background.x = width / 2;
                this.background.y = height / 2;
            }

            if (this.foreground !== null) {
                this.foreground.scale.set(scaleX, scaleY);
                this.foreground.x = width / 2;
                this.foreground.y = height / 2;
            }

            if (this.player !== null) {
                this.player.scale.set(scaleX, scaleY);
                this.player.x = width / 2;
                this.player.y = height - height / 10;
            }

            for (let i = 0; i < this.obstacles.length; i++) {
                const enemy = this.obstacles[i];
                if (typeof enemy !== 'undefined') {
                    enemy.scale.set(scaleX, scaleY);
                }
            }
        }
    }

    setMuted(muted) {
        this.muted = muted;
    }

    destroy() {
        if (this.app) {
            this.removeEvents();
            this.app.stage.removeChildren();
            this.app.ticker.stop();
            this.app.destroy(true, { children: true });
            this.app = null;
        }

        this.running = false;
        this.started = false;
    }
}

export default Downfall;
